import axios from 'axios';
import { API_CONFIG, STORAGE_KEYS } from '../../constants';
import { ISenderAccount, UpdateSenderAccountRequest, CreateSenderAccountRequest } from 'types/senderAccounts/senderAccount.types';

const SERVER_URL = API_CONFIG.SERVER_URL;

// Create axios instance with default config
const api = axios.create({
    baseURL: SERVER_URL,
});

// Add request interceptor to include token in headers
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
        if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Token expired or invalid, redirect to login
            localStorage.removeItem(STORAGE_KEYS.TOKEN);
            localStorage.removeItem(STORAGE_KEYS.USER);
            window.location.href = '/';
        }
        return Promise.reject(error);
    }
);

export const upsertSenderAccount = async (senderAccount: CreateSenderAccountRequest): Promise<ISenderAccount> => {
    try {
        const response = await api.post('/sender-accounts', senderAccount);
        return response.data.senderAccount;
    } catch (error) {
        console.error('Error upserting sender account:', error);
        throw error;
    }
}

export const updateSenderAccount = async (senderAccount: UpdateSenderAccountRequest): Promise<ISenderAccount> => {
    try {
        const response = await api.patch(`/sender-accounts/${senderAccount.id}`, senderAccount.data);
        return response.data.senderAccount;
    } catch (error) {
        console.error('Error updating sender account:', error);
        throw error;
    }
}

export const getSenderAccountById = async (id: string): Promise<ISenderAccount> => {
    try {
        const response = await api.get(`/sender-accounts/${id}`);
        return response.data.senderAccount;
    } catch (error) {
        console.error('Error fetching sender account:', error);
        throw error;
    }
}

export const deleteSenderAccount = async (id: string): Promise<void> => {
    try {
        await api.delete(`/sender-accounts/${id}`);
    } catch (error) {
        console.error('Error deleting sender account:', error);
        throw error;
    }
}

export const getVerifiedSenderAccounts = async (): Promise<ISenderAccount[]> => {
    try {
        const response = await api.get('/sender-accounts/verified');
        return response.data.senderAccounts;
    } catch (error) {
        console.error('Error fetching verified sender accounts:', error);
        throw error;
    }
}

export default api;